// src/pages/AccountSettingsPage.js
import React from 'react';
import { User, Mail, Key, CheckCircle, XCircle } from 'lucide-react';

function AccountSettingsPage({ user }) {
  if (!user) {
    return (
      <div className="flex-grow flex flex-col items-center justify-center text-center p-8">
        <h2 className="text-3xl font-semibold text-gray-300">Access Denied</h2>
        <p className="text-gray-400 mt-2">Please sign in to view your account settings.</p>
      </div>
    );
  }

  return (
    <div className="flex-grow flex flex-col items-center justify-center p-4 md:p-8 animate-fade-in">
      <div className="bg-slate-800/50 backdrop-blur-md p-8 md:p-12 rounded-3xl shadow-2xl w-full max-w-2xl border border-slate-700/50">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-cyan-400">
            Account Settings
          </h1>
          <p className="text-gray-400">Manage your profile information</p>
        </div>

        {user.photoURL && (
          <div className="flex justify-center mb-8">
            <img
              src={user.photoURL}
              alt="User profile"
              className="w-32 h-32 rounded-full border-4 border-indigo-500/50 shadow-lg shadow-indigo-500/20"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://placehold.co/128x128/6366F1/FFFFFF?text=User";
              }}
            />
          </div>
        )}

        <div className="space-y-6">
          <div className="bg-slate-700/30 p-5 rounded-xl border border-slate-600/50 hover:border-indigo-500/50 transition-colors">
            <div className="flex items-start space-x-4">
              <div className="bg-indigo-500/20 p-3 rounded-lg">
                <User size={24} className="text-indigo-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-400 mb-1">Display Name</p>
                <p className="text-lg text-white font-medium">{user.displayName || 'Not set'}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-700/30 p-5 rounded-xl border border-slate-600/50 hover:border-purple-500/50 transition-colors">
            <div className="flex items-start space-x-4">
              <div className="bg-purple-500/20 p-3 rounded-lg">
                <Mail size={24} className="text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-400 mb-1">Email Address</p>
                <p className="text-lg text-white font-medium break-words">{user.email || 'Not available'}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-700/30 p-5 rounded-xl border border-slate-600/50 hover:border-cyan-500/50 transition-colors">
            <div className="flex items-start space-x-4">
              <div className="bg-cyan-500/20 p-3 rounded-lg">
                <Key size={24} className="text-cyan-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-400 mb-1">User ID</p>
                <p className="text-sm text-white font-mono break-all">{user.uid}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-700/30 p-5 rounded-xl border border-slate-600/50 hover:border-emerald-500/50 transition-colors">
            <div className="flex items-start space-x-4">
              <div className={`${user.emailVerified ? 'bg-emerald-500/20' : 'bg-red-500/20'} p-3 rounded-lg`}>
                {user.emailVerified ? (
                  <CheckCircle size={24} className="text-emerald-400" />
                ) : (
                  <XCircle size={24} className="text-red-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-400 mb-1">Email Verification</p>
                <p className={`text-lg font-semibold ${user.emailVerified ? 'text-emerald-400' : 'text-red-400'}`}>
                  {user.emailVerified ? 'Verified' : 'Not Verified'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountSettingsPage;